import {
  IconCalendar,
  IconClock,
  IconHeart,
  IconHeartFilled,
  IconMapPin,
  IconPhotoFilled,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCountryCoverUrl } from "src/components/features/tours/getCountryImageUrl";
import { getDaysText, getTourText, splitTourName } from "src/components/features/tours/tour-text-utils";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import { getDisplayDateValue, FLIGHT_DATE_FORMAT } from "src/utils/date-utils";
import classes from "./ProductCards.module.css";

// Same info blocks as desktop's BusTourFeaturedCard/GridCard (subtitle landmarks, country/date/
// duration row, divider, "Cena"/"Cena no" price) — was title+country+price only before, which
// left the card looking empty next to the richer Vacation/FlightHotel mobile cards.
export function BusTourDealCard({ tour }) {
  const { getStringRes, currencySymbol } = useLocaleContext();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const imageUrl = getCountryCoverUrl(tour.country, tour.code);
  const hasImage = !imgError && !!imageUrl;
  const { title, subtitle } = splitTourName(tour.name);
  const favoriteId = `tour-${tour.code}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "tour", data: tour }));
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  return (
    <div className={classes.busFavoriteWrap}>
      <button
        type="button"
        className={classes.busFavoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={14} color="#ff2965" />
        ) : (
          <IconHeart size={14} stroke={1.75} />
        )}
      </button>
      <Link to={`/bus-tours/${tour.code}`} className={classes.busCard}>
        <div className={classes.busImageWrap}>
          {hasImage ? (
            <img
              src={imageUrl}
              alt={tour.country}
              className={classes.busImage}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={classes.vacationImageFallback}>
              <IconPhotoFilled size="35%" color="var(--ui-4)" />
            </div>
          )}
          {tour.departureDate && (
            <span className={classes.dateBadge}>
              {getDisplayDateValue(tour.departureDate, FLIGHT_DATE_FORMAT)}
            </span>
          )}
        </div>
        <div className={classes.busBody}>
          <h3 className={classes.busName}>{title}</h3>
          {subtitle && <p className={classes.busSubtitle}>{subtitle}</p>}

          <div className={classes.busMetaRow}>
            <span className={classes.busMetaItem}>
              <IconMapPin size={11} stroke={1.75} />
              {tour.country}
            </span>
            {tour.departureDate && (
              <span className={classes.busMetaItem}>
                <IconCalendar size={11} stroke={1.75} />
                {getDisplayDateValue(tour.departureDate, FLIGHT_DATE_FORMAT)}
              </span>
            )}
            {Number.isFinite(tour.days) && (
              <span className={classes.busMetaItem}>
                <IconClock size={11} stroke={1.75} />
                {getDaysText(tour.days, getStringRes)}
              </span>
            )}
          </div>

          <hr className={classes.busDivider} />

          <div className={classes.priceRow}>
            {Number.isFinite(tour.price) ? (
              <>
                <span className={classes.priceLabel}>
                  {tour.dateCount > 1
                    ? getStringRes("cheap.flights.price.from")
                    : getTourText(getStringRes, "tours.card.only")}
                </span>
                <span className={classes.priceAmountSm}>
                  {tour.price.toFixed(0)} {currencySymbol}
                </span>
              </>
            ) : (
              <span className={classes.priceNone}>{getStringRes("cheap.hotels.price.none")}</span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
